/* eslint-disable */
import { createElement, lazy } from 'react';
import { Redirect } from 'react-router-dom';
import { USER_ROLE } from 'constants.js';
import { DEFAULT_PATHS } from 'config.js';
import { getDashboardPath } from 'routing/helper';

const admin = {
  login: lazy(() => import('views/admin/Login')),
  dashboard: lazy(() => import('views/admin/Dashboard')),
  users: lazy(() => import('views/admin/Users')),
  register: lazy(() => import('views/admin/Register')),
};

const defaultPages = {
  login: lazy(() => import('views/default/Login')),
  register: lazy(() => import('views/default/RegisterEnhanced')),
  forgotPassword: lazy(() => import('views/default/ForgotPassword')),
  resetPassword: lazy(() => import('views/default/ResetPassword')),
};

const landing = {
  home: lazy(() => import('views/landing/Home')),
  about: lazy(() => import('views/landing/About')),
  services: lazy(() => import('views/landing/Services')),
  howItWorks: lazy(() => import('views/landing/HowItWorks')),
  news: lazy(() => import('views/landing/News')),
  articleDetail: lazy(() => import('views/landing/ArticleDetail')),
  contact: lazy(() => import('views/landing/Contact')),
  privacyPolicy: lazy(() => import('views/landing/PrivacyPolicy')),
  plansPrograms: lazy(() => import('views/landing/PlansPrograms')),
  termsOfService: lazy(() => import('views/landing/TermsOfService')),
  cookiePolicy: lazy(() => import('views/landing/CookiePolicy')),
};

// Keep the legacy /blog link working, but use the shared Dova article experience.
const blog = lazy(() => import('views/landing/News'));
const community = {
  index: lazy(() => import('views/community/Community')),
  list: lazy(() => import('views/community/CommunityList')),
};
const upgrade = lazy(() => import('views/upgrade/Upgrade'));

const dashboard = {
  gettingStarted: lazy(() => import('views/dashboard/DashboardGettingStarted')),
  analysis: lazy(() => import('views/dashboard/DashboardAnalysis')),
};
const services = {
  database: lazy(() => import('views/services/ServicesDatabase')),
  databaseAdd: lazy(() => import('views/services/ServicesDatabaseAdd')),
  databaseDetail: lazy(() => import('views/services/ServicesDatabaseDetail')),
  storage: lazy(() => import('views/services/ServicesStorage')),
  storageFolder: lazy(() => import('views/services/ServicesStorageFolder')),
  hosting: lazy(() => import('views/services/ServicesHosting')),
  users: lazy(() => import('views/services/ServicesUsers')),
};
const account = {
  settings: lazy(() => import('views/account/AccountSettings')),
  billing: lazy(() => import('views/account/AccountBilling')),
  security: lazy(() => import('views/account/AccountSecurity')),
};
const support = {
  docs: lazy(() => import('views/support/SupportDocs')),
  docsDetail: lazy(() => import('views/support/SupportDocsDetail')),
  knowledgeBase: lazy(() => import('views/support/SupportKnowledgeBase')),
  tickets: lazy(() => import('views/support/SupportTickets')),
  ticketsDetail: lazy(() => import('views/support/SupportTicketsDetail')),
};

const provider = {
  dashboard: lazy(() => import('views/provider/ProviderDashboard')),
  services: lazy(() => import('views/provider/ProviderServices')),
  products: lazy(() => import('views/provider/ProviderProducts')),
  subusers: lazy(() => import('views/provider/ProviderSubusers')),
  orders: lazy(() => import('views/provider/ProviderOrders')),
  profileCompletion: lazy(() => import('views/provider/ProviderProfileCompletionEnhanced')),
  serviceAreas: lazy(() => import('views/provider/ProviderServiceAreas')),
  events: lazy(() => import('views/provider/events/EventsList')),
  createEvent: lazy(() => import('views/provider/events/CreateEvent')),
  eventDetail: lazy(() => import('views/provider/events/EventDetail')),
  editEvent: lazy(() => import('views/provider/events/EditEvent')),
  ticketCheckIn: lazy(() => import('views/provider/events/TicketCheckIn')),
  calendarAvailability: lazy(() => import('views/provider/bookings/CalendarAvailability')),
  myBookings: lazy(() => import('views/provider/bookings/MyBookings')),
};

const events = {
  listing: lazy(() => import('views/events/EventsListing')),
  details: lazy(() => import('views/events/EventDetails')),
  registration: lazy(() => import('views/events/EventRegistration')),
  bookingConfirmation: lazy(() => import('views/events/BookingConfirmation')),
  paymentComplete: lazy(() => import('views/events/PaymentComplete')),
  myTickets: lazy(() => import('views/events/MyTickets')),
  ticketVerify: lazy(() => import('views/events/TicketVerify')),
};

const wellness = {
  dashboard: lazy(() => import('views/wellness/WellnessDashboard')),
  organizations: lazy(() => import('views/wellness/OrganizationList')),
  organizationDetails: lazy(() => import('views/wellness/OrganizationDetails')),
  members: lazy(() => import('views/wellness/MemberManagement')),
  programs: lazy(() => import('views/wellness/ProgramList')),
  programDetails: lazy(() => import('views/wellness/ProgramDetails')),
  activityManagement: lazy(() => import('views/wellness/ActivityManagement')),
  createProgram: lazy(() => import('views/wellness/CreateProgram')),
  programTemplateBrowser: lazy(() => import('views/wellness/ProgramTemplateBrowser')),
  screenings: lazy(() => import('views/wellness/QuickScreeningForm')),
  reports: lazy(() => import('views/wellness/ProviderWellnessReports')),
};

const careRequests = {
  list: lazy(() => import('views/care-requests/CareRequestsList')),
  create: lazy(() => import('views/care-requests/CreateCareRequest')),
};

const member = {
  dashboard: lazy(() => import('views/member/MemberDashboard')),
  providers: lazy(() => import('views/member/MyProviders')),
  providerProfile: lazy(() => import('views/member/ProviderProfile')),
  providerList: lazy(() => import('views/member/bookings/ProviderList')),
  bookProvider: lazy(() => import('views/member/bookings/BookProvider')),
  myBookings: lazy(() => import('views/member/bookings/MyBookings')),
};

const corporate = {
  dashboard: lazy(() => import('views/corporate/CorporateDashboard')),
  admins: lazy(() => import('views/corporate/CorporateAdmins')),
  members: lazy(() => import('views/corporate/CorporateMembers')),
  providers: lazy(() => import('views/corporate/ManageProviders')),
  wellnessAnalytics: lazy(() => import('views/corporate/WellnessAnalytics')),
};

const corporateMember = {
  dashboard: lazy(() => import('views/corporate-member/CorporateMemberDashboard')),
};

const health = {
  myAssessments: lazy(() => import('views/health/MyAssessments')),
  assessmentDetails: lazy(() => import('views/health/AssessmentDetails')),
  takeAssessment: lazy(() => import('views/health/TakeAssessment')),
};

const memberWellness = {
  dashboard: lazy(() => import('views/member-wellness/WellnessDashboard')),
  programs: lazy(() => import('views/member-wellness/MyPrograms')),
  programDetails: lazy(() => import('views/member-wellness/ProgramDetails')),
  activities: lazy(() => import('views/member-wellness/Activities')),
  reports: lazy(() => import('views/member-wellness/WellnessReports')),
};

const appRoot = DEFAULT_PATHS.APP.endsWith('/') ? DEFAULT_PATHS.APP.slice(1, DEFAULT_PATHS.APP.length) : DEFAULT_PATHS.APP;

const DashboardRedirect = () => {
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('authData') || '{}').user || null;
  } catch (error) {
    user = null;
  }
  return createElement(Redirect, { to: getDashboardPath(user) });
};

const routesAndMenuItems = {
  mainMenuItems: [
    {
      path: DEFAULT_PATHS.APP,
      exact: true,
      component: landing.home,
      label: 'Home',
    },
    {
      path: `${appRoot}/about`,
      component: landing.about,
      label: 'About',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/services`,
      component: landing.services,
      label: 'Services',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/how-it-works`,
      component: landing.howItWorks,
      label: 'How It Works',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/news`,
      component: landing.news,
      label: 'News',
      hideInMenu: true,
      exact: true,
    },
    {
      path: `${appRoot}/news/:slug`,
      component: landing.articleDetail,
      label: 'Article',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/contact`,
      component: landing.contact,
      label: 'Contact',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/privacy-policy`,
      component: landing.privacyPolicy,
      label: 'Privacy Policy',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/wellness-plans`,
      exact: true,
      component: landing.plansPrograms,
      label: 'Wellness Plans & Programs',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/terms`,
      component: landing.termsOfService,
      label: 'Terms of Service',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/cookie-policy`,
      component: landing.cookiePolicy,
      label: 'Cookie Policy',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events`,
      exact: true,
      component: events.listing,
      label: 'Events',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/booking/:bookingNumber`,
      component: events.bookingConfirmation,
      label: 'Booking Confirmation',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/payment-complete`,
      component: events.paymentComplete,
      label: 'Payment Complete',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/:slug/register`,
      component: events.registration,
      label: 'Sell Tickets',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/my-tickets`,
      component: events.myTickets,
      label: 'My Tickets',
      hideInMenu: true,
      protected: true,
    },
    {
      path: `${appRoot}/events/:slug`,
      component: events.details,
      label: 'Event Details',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/my/tickets`,
      component: events.myTickets,
      label: 'My Tickets',
      hideInMenu: true,
      protected: true,
    },
    {
      path: '/tickets/verify',
      component: events.ticketVerify,
      label: 'Ticket Verification',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/login`,
      component: defaultPages.login,
      label: 'Login',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/register`,
      component: defaultPages.register,
      label: 'Register',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/admin/login`,
      component: admin.login,
      label: 'Admin Login',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/admin/dashboard`,
      component: admin.dashboard,
      label: 'Admin Dashboard',
      hideInMenu: true,
      protected: true,
      roles: [USER_ROLE.Admin],
    },
    {
      path: `${appRoot}/blog`,
      component: blog,
      label: 'menu.blog',
      icon: 'file-text',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/upgrade`,
      label: 'menu.upgrade',
      icon: 'trend-up',
      component: upgrade,
      hideInMenu: true,
    },
    {
      path: `${appRoot}/community`,
      label: 'menu.community',
      icon: 'messages',
      component: community.index,
      hideInMenu: true,
      subs: [
        {
          path: '/list',
          label: 'menu.community-list',
          hideInMenu: true,
          component: community.list,
        },
      ],
    },
    // Account Settings - accessible from profile dropdown only
    {
      path: `${appRoot}/account/settings`,
      component: account.settings,
      label: 'Account Settings',
      hideInMenu: true,
      protected: true,
    },
  ],
  sidebarItems: [
    // Member/Family Dashboard (First for family members)
    {
      path: `${appRoot}/member/dashboard`,
      label: 'menu.member-dashboard',
      icon: 'home',
      exact: true,
      component: member.dashboard,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Corporate Member Dashboard (First for corporate members)
    {
      path: `${appRoot}/corporate-member/dashboard`,
      label: 'Dashboard',
      icon: 'home',
      exact: true,
      component: corporateMember.dashboard,
      protected: true,
      roles: [USER_ROLE.CorporateMember],
    },
    // Health Risk Assessments (Second for family members)
    {
      path: `${appRoot}/health/my-assessments`,
      label: 'Health Risk Assessments',
      icon: 'activity',
      exact: true,
      component: health.myAssessments,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Wellness Programs (For All Members)
    {
      path: `${appRoot}/member-wellness/programs`,
      label: 'Wellness Programs',
      icon: 'heart',
      exact: true,
      component: memberWellness.programs,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Care Requests (Third for family members)
    {
      path: `${appRoot}/care-requests`,
      label: 'Care Requests',
      icon: 'plus',
      exact: true,
      component: careRequests.list,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Consultation Bookings (Fourth for family members)
    {
      path: `${appRoot}/member/bookings/my-bookings`,
      label: 'Consultation Bookings',
      icon: 'calendar',
      exact: true,
      component: member.myBookings,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // My Providers (Fifth for family members)
    {
      path: `${appRoot}/member/providers`,
      label: 'My Providers',
      icon: 'user',
      exact: true,
      component: member.providers,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Users (Sixth for family members)
    {
      path: `${appRoot}/users`,
      label: 'menu.users',
      icon: 'user',
      exact: true,
      component: lazy(() => import('views/users/Users')),
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Events (Seventh for family members - dropdown)
    {
      path: `${appRoot}/events`,
      label: 'Events',
      icon: 'calendar',
      exact: true,
      redirect: true,
      to: `${appRoot}/events`,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
      subs: [
        { path: '', label: 'Browse Events', icon: 'search', component: events.listing },
        { path: '/my-tickets', label: 'My Tickets', icon: 'tag', component: events.myTickets, protected: true },
      ],
    },
    // Book Provider (Hidden from menu)
    {
      path: `${appRoot}/member/bookings/provider/:providerId`,
      label: 'Book Provider',
      icon: 'calendar',
      exact: true,
      component: member.bookProvider,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Provider Profile (Hidden from menu)
    {
      path: `${appRoot}/member/provider/:providerId`,
      label: 'Provider Profile',
      icon: 'user',
      exact: true,
      component: member.providerProfile,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Take Assessment (Hidden from menu)
    {
      path: `${appRoot}/health/take-assessment`,
      label: 'Take Assessment',
      icon: 'clipboard',
      exact: true,
      component: health.takeAssessment,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Assessment Details (Hidden from menu)
    {
      path: `${appRoot}/health/assessment/:id`,
      label: 'Assessment Details',
      icon: 'file-text',
      exact: true,
      component: health.assessmentDetails,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Create Care Request (Hidden from menu)
    {
      path: `${appRoot}/care-requests/:requestId/edit`,
      label: 'Edit Care Request',
      icon: 'edit',
      exact: true,
      component: careRequests.create,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    {
      path: `${appRoot}/care-requests/create`,
      label: 'New Care Request',
      icon: 'plus',
      exact: true,
      component: careRequests.create,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Member Wellness Dashboard (Hidden - merged into main Dashboard)
    {
      path: `${appRoot}/member-wellness/dashboard`,
      label: 'Wellness Dashboard',
      icon: 'grid',
      exact: true,
      component: memberWellness.dashboard,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Program Details (Hidden from menu)
    {
      path: `${appRoot}/member-wellness/programs/:enrollmentId`,
      label: 'Program Details',
      icon: 'heart',
      exact: true,
      component: memberWellness.programDetails,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Activity Tracking
    {
      path: `${appRoot}/member-wellness/activities`,
      label: 'My Activities',
      icon: 'check-circle',
      exact: true,
      component: memberWellness.activities,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Wellness Reports
    {
      path: `${appRoot}/member-wellness/reports`,
      label: 'Wellness Reports',
      icon: 'file-text',
      exact: true,
      component: memberWellness.reports,
      protected: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient, USER_ROLE.CorporateMember],
    },
    // Book Provider List (Hidden from menu)
    {
      path: `${appRoot}/member/bookings/providers`,
      label: 'Book Provider',
      icon: 'calendar',
      exact: true,
      component: member.providerList,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Provider Dashboard (First for providers)
    {
      path: `${appRoot}/provider/dashboard`,
      label: 'Provider Dashboard',
      icon: 'health',
      exact: true,
      component: provider.dashboard,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Profile (Second for providers - moved to top)
    {
      path: `${appRoot}/provider/profile`,
      label: 'My Profile',
      icon: 'file-text',
      exact: true,
      component: provider.profileCompletion,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Services (Third for providers)
    {
      path: `${appRoot}/provider/services`,
      label: 'My Services',
      icon: 'diagram-1',
      exact: true,
      component: provider.services,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    {
      path: `${appRoot}/provider/service-areas`,
      label: 'Service Areas',
      icon: 'pin',
      exact: true,
      component: provider.serviceAreas,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    {
      path: `${appRoot}/provider/calendar-availability`,
      label: 'Calendar & Availability',
      icon: 'clock',
      exact: true,
      component: provider.calendarAvailability,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Bookings (moved here - grouped with scheduling)
    {
      path: `${appRoot}/provider/bookings`,
      label: 'My Bookings',
      icon: 'notebook-1',
      exact: true,
      component: provider.myBookings,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Products (Fourth for providers)
    {
      path: `${appRoot}/provider/products`,
      label: 'My Products',
      icon: 'shop',
      exact: true,
      component: provider.products,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Orders (Fifth for providers)
    {
      path: `${appRoot}/provider/orders`,
      label: 'My Orders',
      icon: 'cart',
      exact: true,
      component: provider.orders,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Subusers (Fifth for providers)
    {
      path: `${appRoot}/provider/subusers`,
      label: 'My Subusers',
      icon: 'user',
      exact: true,
      component: provider.subusers,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Events (Sixth for providers)
    {
      path: `${appRoot}/provider/events`,
      label: 'My Events',
      icon: 'calendar',
      exact: true,
      component: provider.events,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // Ticket Check-in (Hidden from menu)
    {
      path: `${appRoot}/provider/events/check-in`,
      label: 'Check In Tickets',
      icon: 'qr-code',
      component: provider.ticketCheckIn,
      protected: true,
      roles: [USER_ROLE.Provider],
      hideInMenu: true,
    },
    // My Bookings (Eighth for providers)
    {
      path: `${appRoot}/provider/bookings`,
      label: 'My Bookings',
      icon: 'notebook-1',
      exact: true,
      component: provider.myBookings,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // Wellness and Tests (Ninth for providers)
    {
      path: `${appRoot}/wellness`,
      label: 'Wellness and Tests',
      icon: 'activity',
      exact: true,
      redirect: true,
      to: `${appRoot}/wellness/dashboard`,
      protected: true,
      roles: [USER_ROLE.Provider],
      subs: [
        {
          path: '/dashboard',
          label: 'Wellness Dashboard',
          icon: 'grid-1',
          component: wellness.dashboard,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/screenings/new',
          label: 'Health Screening',
          icon: 'activity',
          component: wellness.screenings,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/organizations',
          label: 'Corporate Programs',
          icon: 'building',
          component: wellness.organizations,
          exact: true,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/organizations/:organizationId/programs/new',
          label: 'Create Program',
          icon: 'plus',
          component: wellness.createProgram,
          hideInMenu: true,
          protected: true,
        },
        {
          path: '/organizations/:organizationId/programs',
          label: 'Programs',
          icon: 'activity',
          component: wellness.programs,
          exact: true,
          hideInMenu: true,
          protected: true,
        },
        {
          path: '/organizations/:organizationId/program-templates',
          label: 'Browse Program Templates',
          icon: 'book-open',
          component: wellness.programTemplateBrowser,
          hideInMenu: true,
          protected: true,
        },
        {
          path: '/organizations/:organizationId/members',
          label: 'Members',
          icon: 'users',
          component: wellness.members,
          hideInMenu: true,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/organizations/:organizationId',
          label: 'Organization Details',
          icon: 'building',
          component: wellness.organizationDetails,
          hideInMenu: true,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/programs/:programId',
          label: 'Program Details',
          icon: 'activity',
          component: wellness.programDetails,
          hideInMenu: true,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/activity-management/:enrollmentId',
          label: 'Activity Management',
          icon: 'trending-up',
          component: wellness.activityManagement,
          hideInMenu: true,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
        {
          path: '/reports',
          label: 'Reports & Analytics',
          icon: 'file-text',
          component: wellness.reports,
          protected: true,
          roles: [USER_ROLE.Provider],
        },
      ],
    },
    // Create Event (Hidden from menu)
    {
      path: `${appRoot}/provider/events/create`,
      label: 'Create Event',
      icon: 'plus',
      component: provider.createEvent,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    // Edit Event (Hidden from menu)
    {
      path: `${appRoot}/provider/events/:id/edit`,
      label: 'Edit Event',
      icon: 'edit',
      component: provider.editEvent,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    // Event Detail (Hidden from menu)
    {
      path: `${appRoot}/provider/events/:id`,
      label: 'Event Detail',
      icon: 'eye',
      component: provider.eventDetail,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    // Corporate Admin Dashboard (First for corporate admins)
    {
      path: `${appRoot}/corporate/dashboard`,
      label: 'Organization Dashboard',
      icon: 'building',
      exact: true,
      component: corporate.dashboard,
      protected: true,
      roles: [USER_ROLE.CorporateAdmin],
    },
    // Manage Admins (Second for corporate admins)
    {
      path: `${appRoot}/corporate/admins`,
      label: 'Manage Admins',
      icon: 'user',
      exact: true,
      component: corporate.admins,
      protected: true,
      roles: [USER_ROLE.CorporateAdmin],
    },
    // Manage Members (Third for corporate admins)
    {
      path: `${appRoot}/corporate/members`,
      label: 'Manage Members',
      icon: 'user',
      exact: true,
      component: corporate.members,
      protected: true,
      roles: [USER_ROLE.CorporateAdmin],
    },
    // Manage Providers (Fourth for corporate admins)
    {
      path: `${appRoot}/corporate/providers`,
      label: 'Wellness Providers',
      icon: 'heart',
      exact: true,
      component: corporate.providers,
      protected: true,
      roles: [USER_ROLE.CorporateAdmin],
    },
    // Wellness Analytics (Fifth for corporate admins)
    {
      path: `${appRoot}/corporate/wellness-analytics`,
      label: 'Wellness Analytics',
      icon: 'chart-4',
      exact: true,
      component: corporate.wellnessAnalytics,
      protected: true,
      roles: [USER_ROLE.CorporateAdmin],
    },
    {
      path: `${appRoot}/dashboard`,
      label: 'menu.dashboard',
      icon: 'grid-1',
      exact: true,
      component: DashboardRedirect,
      hideInMenu: true,
    },
    // Temporarily commented out - will activate as features are developed
    /*
    {
      path: `${appRoot}/services`,
      label: 'menu.services',
      icon: 'grid-1',
      exact: true,
      redirect: true,
      to: `${appRoot}/services/database`,
      subs: [
        {
          path: '/database',
          label: 'menu.database',
          icon: 'database',
          component: services.database,
          subs: [
            { path: '/add', label: 'menu.database-add', hideInMenu: true, component: services.databaseAdd },
            { path: '/detail', label: 'menu.database-detail', hideInMenu: true, component: services.databaseDetail },
          ],
        },
        {
          path: '/storage',
          label: 'menu.storage',
          icon: 'file-image',
          component: services.storage,
          subs: [{ path: '/folder', label: 'menu.storage', hideInMenu: true, component: services.storageFolder }],
        },
        { path: '/storage-folder', component: services.storageFolder, hideInMenu: true },
        { path: '/hosting', label: 'menu.hosting', icon: 'router', component: services.hosting },
        { path: '/users', label: 'menu.users', icon: 'user', component: services.users },
      ],
    },
    */
    // Temporarily commented out - will activate as features are developed
    /*
    {
      path: `${appRoot}/support`,
      label: 'menu.support',
      icon: 'help',
      exact: true,
      redirect: true,
      to: `${appRoot}/support/docs`,
      subs: [
        {
          path: '/docs',
          label: 'menu.docs',
          icon: 'file-empty',
          component: support.docs,
          subs: [{ path: '/detail', label: 'menu.docs', icon: 'file-empty', component: support.docsDetail, hideInMenu: true }],
        },
        { path: '/knowledge-base', label: 'menu.knowledge-base', icon: 'notebook-1', component: support.knowledgeBase },
        {
          path: '/tickets',
          label: 'menu.tickets',
          icon: 'bookmark',
          component: support.tickets,
          subs: [{ path: '/detail', label: 'menu.tickets-detail', icon: 'file-empty', component: support.ticketsDetail, hideInMenu: true }],
        },
      ],
    },
    */
  ],
};
export default routesAndMenuItems;
